import type { User as BackendUser } from '../dal/user-dal'

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated'

export type BackendUserStatus =
  | 'idle'
  | 'loading'
  | 'missing'
  | 'ready'
  | 'error'

export type ProtectedRouteDecision =
  | 'loading'
  | 'onboarding'
  | 'create-user'
  | 'error'
  | 'allow'

export type AccountRouteDecision =
  | 'loading'
  | 'onboarding'
  | 'create-user'
  | 'error'
  | 'purchase'
  | 'post-payment-onboarding'
  | 'main'

export type BackendUserWithRequiredIds = BackendUser & {
  revenueCatId: string
  created: Date
}

export interface StorageScopeResolution {
  targetStorageScopeKey: string | null
  nextScopeUserId: string | null
  isReady: boolean
  shouldFetchBackendUser: boolean
}

export function hasRequiredBackendUserIds(
  backendUser: BackendUser | null | undefined,
): backendUser is BackendUserWithRequiredIds {
  return (
    backendUser != null &&
    typeof backendUser.revenueCatId === 'string' &&
    backendUser.revenueCatId.trim().length > 0 &&
    backendUser.created != null &&
    backendUser.created instanceof Date
  )
}

export function resolveRequiresPostPaymentOnboarding({
  hasPurchased,
  backendUser,
}: {
  hasPurchased: boolean
  backendUser:
    | Pick<BackendUser, 'onboardingCompleted' | 'voiceType'>
    | null
    | undefined
}): boolean {
  if (!hasPurchased || backendUser == null) {
    return false
  }

  return backendUser.voiceType == null
}

interface ResolveBackendUserStatusParams {
  shouldFetchBackendUser: boolean
  backendUser: BackendUser | null | undefined
  backendUserError: unknown
  isPending: boolean
  isFetching: boolean
}

export function resolveBackendUserStatus({
  shouldFetchBackendUser,
  backendUser,
  backendUserError,
  isPending,
  isFetching,
}: ResolveBackendUserStatusParams): BackendUserStatus {
  if (!shouldFetchBackendUser) {
    return 'idle'
  }

  if (backendUser != null) {
    return 'ready'
  }

  if (backendUser === null) {
    return 'missing'
  }

  if (isPending || isFetching) {
    return 'loading'
  }

  if (backendUserError != null) {
    return 'error'
  }

  return 'idle'
}

interface ResolveProtectedRouteParams {
  authStatus: AuthStatus
  backendUserStatus: BackendUserStatus
  needsUserCreation: boolean
}

export function resolveProtectedRoute({
  authStatus,
  backendUserStatus,
  needsUserCreation,
}: ResolveProtectedRouteParams): ProtectedRouteDecision {
  if (authStatus === 'loading' || backendUserStatus === 'loading') {
    return 'loading'
  }

  if (authStatus === 'anonymous') {
    return 'onboarding'
  }

  if (backendUserStatus === 'ready') {
    return 'allow'
  }

  if (needsUserCreation) {
    return 'create-user'
  }

  if (backendUserStatus === 'error') {
    return 'error'
  }

  return 'allow'
}

export function resolveAccountRoute(params: {
  protectedRoute: ProtectedRouteDecision
  requiresPostPaymentOnboarding: boolean
  isAccessPending: boolean
  hasPurchased: boolean
  backendUserIdsReady: boolean
  paywallSkipped: boolean
}): AccountRouteDecision {
  const {
    protectedRoute,
    requiresPostPaymentOnboarding,
    isAccessPending,
    hasPurchased,
    backendUserIdsReady,
    paywallSkipped,
  } = params

  if (protectedRoute !== 'allow') {
    return protectedRoute
  }

  if (isAccessPending) {
    return 'loading'
  }

  if (!hasPurchased && !paywallSkipped) {
    return 'purchase'
  }

  if (requiresPostPaymentOnboarding) {
    return 'post-payment-onboarding'
  }

  if (!backendUserIdsReady) {
    return 'loading'
  }

  return 'main'
}

export type RevenueCatGateStatus =
  | 'idle'
  | 'initializing'
  | 'identifying'
  | 'ready'
  | 'failed'

export function resolveAccessPending(params: {
  backendUserStatus: BackendUserStatus
  hasBackendEntitlement: boolean
  revenueCatInitStatus: RevenueCatGateStatus
  isCustomerInfoFetching: boolean
}): boolean {
  if (params.backendUserStatus !== 'ready') {
    return false
  }

  if (params.hasBackendEntitlement) {
    return false
  }

  if (
    params.revenueCatInitStatus === 'idle' ||
    params.revenueCatInitStatus === 'failed'
  ) {
    return false
  }

  if (params.revenueCatInitStatus !== 'ready') {
    return true
  }

  return params.isCustomerInfoFetching
}

export function resolveStorageScope(params: {
  authStatus: AuthStatus
  authUserId: string | null
  syncedStorageScopeKey: string | null | undefined
}): StorageScopeResolution {
  const { authStatus, authUserId, syncedStorageScopeKey } = params

  if (authStatus === 'loading') {
    return {
      targetStorageScopeKey: null,
      nextScopeUserId: null,
      isReady: false,
      shouldFetchBackendUser: false,
    }
  }

  if (authStatus !== 'authenticated') {
    return {
      targetStorageScopeKey: null,
      nextScopeUserId: null,
      isReady: syncedStorageScopeKey === null,
      shouldFetchBackendUser: false,
    }
  }

  if (authUserId == null) {
    throw new Error(
      'Authenticated app session is missing the Auth0 user identifier.',
    )
  }

  const targetStorageScopeKey = `authenticated:${authUserId}`

  return {
    targetStorageScopeKey,
    nextScopeUserId: authUserId,
    isReady: syncedStorageScopeKey === targetStorageScopeKey,
    shouldFetchBackendUser: syncedStorageScopeKey === targetStorageScopeKey,
  }
}
